function n(value) {
  return Number(value || 0) || 0;
}

export function getElapsedRotaDayLimit(date = new Date()) {
  return Math.max(0, date.getDate() - 1);
}

function fmt(value, digits = 1) {
  return n(value).toFixed(digits);
}

function fmtDay(value) {
  const number = n(value);
  return Number.isInteger(number) ? String(number) : fmt(number);
}

function daySource(engineer) {
  if (engineer?.daySource) return engineer.daySource;
  return n(engineer.rotaWorkedDays) > 0 ? "rota" : "saved";
}

function daySourceLabel(source) {
  return source === "rota" ? "rota-adjusted" : "saved";
}

function defaultTiersForTeam(team, tiersByTeam) {
  return [...(tiersByTeam?.[team] || tiersByTeam?.default || [])].sort(
    (a, b) => n(a.daily) - n(b.daily),
  );
}

function berShare(engineer) {
  return n(engineer.totalParts) > 0
    ? (n(engineer.ber) / n(engineer.totalParts)) * 100
    : 0;
}

function bonusCountingParts(engineer) {
  const bonusEligible = n(engineer.bonusEligible);
  if (bonusEligible > 0) {
    return {
      value: bonusEligible,
      label: "bonus-eligible parts",
      source: "bonusEligible",
    };
  }
  return {
    value: n(engineer.nonBer),
    label: "bonus-counting parts",
    source: "nonBer",
  };
}

export function calculateHistoryEffectiveDays(engineer) {
  const hasElapsedRota = Object.prototype.hasOwnProperty.call(
    engineer || {},
    "elapsedRotaWorkedDays",
  );
  const rotaWorkedDays = n(engineer?.rotaWorkedDays);
  const elapsedRotaWorkedDays = n(engineer?.elapsedRotaWorkedDays);
  const savedDays = n(engineer?.daysWorked);

  if (rotaWorkedDays > 0) {
    const effectiveDays = hasElapsedRota
      ? elapsedRotaWorkedDays
      : rotaWorkedDays;
    return {
      effectiveDays,
      daySource: "rota",
      usingElapsedRota:
        hasElapsedRota &&
        Math.abs(elapsedRotaWorkedDays - rotaWorkedDays) >= 0.01,
    };
  }

  return {
    effectiveDays: savedDays,
    daySource: "saved",
    usingElapsedRota: false,
  };
}

export function buildEngineerDailyAverageRows(engineers, options = {}) {
  const limit = Math.max(1, n(options.limit || 8));
  return [...(engineers || [])]
    .map((engineer) => {
      const days = n(engineer.effectiveDays);
      const countingParts = bonusCountingParts(engineer);
      const average =
        days > 0 ? countingParts.value / days : n(engineer.bonusAvg);
      const actualAverage =
        days > 0 ? n(engineer.totalParts) / days : n(engineer.avgPerDay);
      const source = daySource(engineer);
      const sourceLabel = daySourceLabel(source);
      const savedDays = n(engineer.daysWorked);
      const rotaDays = n(engineer.rotaWorkedDays);
      const mismatch = rotaDays > 0 && Math.abs(savedDays - rotaDays) >= 0.5;

      return {
        engineer: engineer.engineer || "Unknown",
        team: engineer.team || "Unknown",
        parts: n(engineer.totalParts),
        countingParts: countingParts.value,
        countingSource: countingParts.source,
        average,
        actualAverage,
        days,
        savedDays,
        rotaDays,
        daySource: source,
        mismatch,
        label: `${fmt(average)}/day`,
        detail:
          days > 0
            ? `${countingParts.value.toLocaleString()} ${countingParts.label} over ${fmtDay(days)} ${sourceLabel} days`
            : "No worked-day denominator available",
        actualAverageLabel: `${fmt(actualAverage)}/day actual completed`,
        actualAverageDetail:
          days > 0
            ? `${n(engineer.totalParts).toLocaleString()} total parts over ${fmtDay(days)} ${sourceLabel} days`
            : "Actual completed average unavailable",
        totalPartsDetail: `${n(engineer.totalParts).toLocaleString()} total parts`,
      };
    })
    .sort((a, b) => {
      const byAverage = b.average - a.average;
      if (byAverage) return byAverage;
      return b.parts - a.parts;
    })
    .slice(0, limit);
}

export function buildHistoryPerformanceCoach(engineers, options = {}) {
  const limit = Math.max(1, n(options.limit || 6));
  const tiersByTeam = options.tiersByTeam || {};

  return [...(engineers || [])]
    .map((engineer) => {
      const days = n(engineer.effectiveDays) || n(engineer.bonusDays);
      const bonusAvg =
        n(engineer.bonusAvg) ||
        (days > 0 ? n(engineer.bonusEligible) / days : 0);
      const tiers = defaultTiersForTeam(engineer.team, tiersByTeam);
      const nextTier = tiers.find((tier) => bonusAvg < n(tier.daily)) || null;
      const requiredMorePerDay = nextTier
        ? Math.max(0, n(nextTier.daily) - bonusAvg)
        : 0;
      const requiredMoreParts = Math.ceil(requiredMorePerDay * Math.max(0, days));
      const shareBer = berShare(engineer);
      const notes = [];
      const savedDays = n(engineer.daysWorked);
      const rotaDays = n(engineer.rotaWorkedDays);

      if (rotaDays > 0 && Math.abs(savedDays - rotaDays) >= 0.5) {
        notes.push(`Saved days differ from rota by ${fmt(Math.abs(savedDays - rotaDays))}`);
      }
      if (shareBer >= 20) notes.push(`BER share is ${fmt(shareBer)}%`);
      if (!days) notes.push("No worked-day denominator");

      return {
        engineer: engineer.engineer || "Unknown",
        team: engineer.team || "Unknown",
        status: nextTier ? "gap" : "top-tier",
        tone: nextTier
          ? requiredMorePerDay > 3 || requiredMoreParts >= 30
            ? "risk"
            : "warning"
          : "good",
        currentAvg: bonusAvg,
        totalAvg: days > 0 ? n(engineer.totalParts) / days : n(engineer.avgPerDay),
        days,
        bonusEligible: n(engineer.bonusEligible),
        nextTierDaily: nextTier ? n(nextTier.daily) : null,
        nextTierBonus: nextTier ? n(nextTier.bonus) : null,
        requiredMorePerDay,
        requiredMoreParts,
        berShare: shareBer,
        notes,
        action: nextTier
          ? `Needs ${fmt(requiredMorePerDay)} more bonus-eligible parts/day for the GBP ${n(nextTier.bonus)} tier.`
          : "Already pacing above the top configured bonus tier.",
      };
    })
    .sort((a, b) => {
      if (a.status !== b.status) return a.status === "gap" ? -1 : 1;
      const byGap = b.requiredMorePerDay - a.requiredMorePerDay;
      if (byGap) return byGap;
      const byParts = b.requiredMoreParts - a.requiredMoreParts;
      if (byParts) return byParts;
      return b.currentAvg - a.currentAvg;
    })
    .slice(0, limit);
}
